/**
 * Single source of truth for technology capabilities on the frontend.
 *
 * COUPLED with the backend registry `TECHNOLOGIES` in app/services/technology_service.py:
 * the two mirror each other (which technologies exist, prefixes, capability flags) and must
 * stay in sync — a change here almost always needs the same change there.
 *
 * Add or change a technology here; the derived exports follow.
 */
export const TECHNOLOGIES = {
  solar: {
    name: "Solar",
    prefix: "T01",
    projected: true,
    forwardEligible: true,
    buyEligible: true,
    re100: true,
  },
  wind: {
    name: "Wind",
    prefix: "T02",
    projected: true,
    forwardEligible: true,
    buyEligible: true,
    re100: true,
  },
  hydro: {
    name: "Hydro",
    prefix: "T03",
    projected: true,
    forwardEligible: true,
    buyEligible: true,
    re100: false,
  },
  marine: {
    name: "Marine",
    prefix: "T04",
    projected: false,
    forwardEligible: false,
    buyEligible: false,
    re100: false,
  },
  biomass: {
    name: "Biomass",
    prefix: "T05",
    projected: true,
    forwardEligible: false,
    buyEligible: true,
    re100: false,
  },
  nuclear: {
    name: "Nuclear",
    prefix: "T06",
    projected: true,
    forwardEligible: false,
    buyEligible: true,
    re100: false,
  },
} as const;

export type TechnologyKey = keyof typeof TECHNOLOGIES;

/** Energy technologies with a production projection (excludes marine). */
export type EnergyTechnology = {
  [K in TechnologyKey]: (typeof TECHNOLOGIES)[K]["projected"] extends true
    ? K
    : never;
}[TechnologyKey];

export const TECHNOLOGY_KEYS = Object.keys(TECHNOLOGIES) as TechnologyKey[];

/**
 * The AIB technology code prefix a technology is identified by on the wire.
 *
 * Registries name a device's technology with an AIB code (`T020001`), and every backend filter —
 * the forwards eligibility criteria among them — matches those codes by family prefix. A key such
 * as `"wind"` is a frontend display word and matches nothing server-side, so anything travelling to
 * the backend as a technology goes through here (RV-picker-01).
 */
export const technologyPrefix = (key: TechnologyKey): string =>
  TECHNOLOGIES[key].prefix;

/** Whether a string from the wire is one of the registry's technology keys. */
export const isTechnologyKey = (key: string): key is TechnologyKey =>
  Object.hasOwn(TECHNOLOGIES, key);

/** Display name for a technology key ("wind" → "Wind"); an unknown key is shown as it came. */
export const technologyName = (key: string): string =>
  isTechnologyKey(key) ? TECHNOLOGIES[key].name : key;

/** The technology an AIB code or code prefix belongs to, or null when none claims it. */
export const technologyKeyForCode = (code: string): TechnologyKey | null =>
  TECHNOLOGY_KEYS.find((key) => code.startsWith(TECHNOLOGIES[key].prefix)) ??
  null;

/** Display name for an AIB code or code prefix ("T02" → "Wind"); unknown codes are shown as they came. */
export const technologyNameForCode = (code: string): string => {
  const key = technologyKeyForCode(code);
  return key === null ? code : TECHNOLOGIES[key].name;
};

/** Projected energy technologies in registry order. */
export const ENERGY_TECHNOLOGIES = TECHNOLOGY_KEYS.filter(
  (key): key is EnergyTechnology => TECHNOLOGIES[key].projected
);

/**
 * Forward-eligible technologies in chart display order. Membership is gated by the
 * registry `forwardEligible` flag; the order is a display choice.
 */
export const FORWARD_ELIGIBLE_TECHNOLOGIES = (
  ["wind", "solar", "hydro"] as const
).filter((key) => TECHNOLOGIES[key].forwardEligible);

/** Atomic technologies offered as buy-side options. */
export type BuyEligibleTechnology = {
  [K in TechnologyKey]: (typeof TECHNOLOGIES)[K]["buyEligible"] extends true
    ? K
    : never;
}[TechnologyKey];

/** Buy-eligible technologies in registry (display) order. */
export const BUY_ELIGIBLE_TECHNOLOGIES = TECHNOLOGY_KEYS.filter(
  (key): key is BuyEligibleTechnology => TECHNOLOGIES[key].buyEligible
);

/** Technologies eligible for strict RE100 sourcing (membership lookup). */
export const RE100_TECHNOLOGIES: readonly string[] = TECHNOLOGY_KEYS.filter(
  (key) => TECHNOLOGIES[key].re100
);
